<template>
  <div>
    <v-card :disabled="isProcessing" :loading="isProcessing">
      <template v-slot:progress>
        <v-progress-linear
          absolute
          color="success"
          height="4"
          indeterminate
        ></v-progress-linear>
      </template>
      <v-tabs v-model="tab" class="crm-color" left dark icons-and-text>
        <v-tabs-slider></v-tabs-slider>
        <v-tab href="#tab-1">
          <v-toolbar-title>{{ title }}</v-toolbar-title>
        </v-tab>
        <v-spacer></v-spacer>
        <div class="buttons-tab"></div>
      </v-tabs>
      <v-tabs-items v-model="tab">
        <v-tab-item value="tab-1">
          <v-card flat>
            <v-card-text>
              <v-form @submit.prevent ref="requestData" v-model="validRequestData">
                <v-row>
                  <v-col cols="12" sm="8">
                    <v-text-field
                      v-model="requestData.name"
                      :label="$t('GENERAL.LABELS.NAME') + '*'"
                      required
                      :rules="[validations.required()]"
                      :error-messages="validationErrors.name || []"
                      @input="clearErrors"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-checkbox
                      class="mt-0 mt-sm-5"
                      v-model="requestData.active"
                      :label="$t('GENERAL.LABELS.ACTIVE')"
                      hide-details
                      color="primary"
                      @change="clearErrors"
                    />
                  </v-col>
                </v-row>

                <v-subheader class="px-0">{{ $t("PIPELINES.STAGES") }}</v-subheader>
                <v-row v-for="(stage, index) in stages" :key="stage._key">
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="stage.name"
                      :label="$t('PIPELINES.STAGE_NAME') + '*'"
                      :rules="[validations.required()]"
                      :error-messages="stageErrors(index, 'name')"
                      @input="clearErrors"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="3">
                    <v-text-field
                      v-model.number="stage.order"
                      type="number"
                      min="0"
                      step="1"
                      :label="$t('GENERAL.LABELS.ORDER') + '*'"
                      :rules="stageOrderRules"
                      :error-messages="stageErrors(index, 'order')"
                      @input="clearErrors"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="3" class="d-flex align-center">
                    <v-btn
                      v-if="stages.length > 2"
                      icon
                      color="error"
                      :aria-label="$t('GENERAL.LABELS.DELETE')"
                      @click="removeStage(index)"
                    >
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
                <v-btn text color="primary" class="mt-2" @click="addStage">
                  {{ $t("PIPELINES.ADD_STAGE") }}
                </v-btn>
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="warning" @click="back">{{ $t("GENERAL.LABELS.BACK_BUTTON") }}</v-btn>
              <v-btn color="success" @click="store()">{{ actionText }}</v-btn>
            </v-card-actions>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </div>
</template>

<script>
import validations from "@/views/partials/form/Validations.vue";
import { SHOW_SNACK } from "@/store/snack.module";
import {
  CREATE_PIPELINE,
  GET_PIPELINE,
  GET_PIPELINE_OPTIONS,
  UPDATE_PIPELINE,
} from "@/store/pipelines.module";
import userStateService from "@/common/userState.service.js";

let stageKeySeq = 0;

export default {
  name: "EditPipeline",
  data() {
    return {
      validations,
      tab: "tab-1",
      isProcessing: false,
      validRequestData: false,
      validationErrors: {},
      title: this.$t("PIPELINES.CREATE"),
      actionText: this.$t("GENERAL.LABELS.CREATE_BUTTON"),
      requestData: {
        name: "",
        active: true,
      },
      stages: [
        { _key: ++stageKeySeq, name: "", order: 1 },
        { _key: ++stageKeySeq, name: "", order: 2 },
      ],
    };
  },
  computed: {
    pipelineEditId() {
      return this.$route.params.id;
    },
    isEditMode() {
      return Boolean(this.pipelineEditId);
    },
    stageOrderRules() {
      return [
        (v) =>
          v === 0 ||
          v === "0" ||
          (v !== "" && v !== null && v !== undefined) ||
          this.$t("AUTH.VALIDATION.REQUIRED_FIELD"),
        (v) => {
          const n = Number(v);
          return Number.isInteger(n) || this.$t("PIPELINES.STAGE_ORDER_INTEGER");
        },
        (v) => Number(v) >= 0 || this.$t("PIPELINES.STAGE_ORDER_MIN"),
      ];
    },
  },
  mounted() {
    if (this.isEditMode) {
      this.loadPipeline();
    }
  },
  methods: {
    minTwoStages(loaded) {
      const rows = [...loaded];
      let next =
        rows.reduce((m, r) => Math.max(m, Number(r.order) || 0), -1) + 1;
      while (rows.length < 2) {
        rows.push({ _key: ++stageKeySeq, name: "", order: next++ });
      }
      return rows;
    },
    loadPipeline() {
      this.isProcessing = true;
      this.title = this.$t("PIPELINES.EDIT");
      this.actionText = this.$t("GENERAL.LABELS.EDIT_BUTTON");
      this.$store
        .dispatch(GET_PIPELINE, this.pipelineEditId)
        .then((response) => {
          this.requestData = {
            name: response.name || "",
            active: !!response.active,
          };
          const loaded = (response.stages || []).map((s) => ({
            _key: s.id,
            id: s.id,
            name: s.name || "",
            order: Number.isFinite(Number(s.order)) ? Number(s.order) : 0,
          }));
          this.stages =
            loaded.length >= 2 ? loaded : this.minTwoStages(loaded);
        })
        .catch((error) => {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.getErrorMessage(error),
            type: "error",
          });
        })
        .finally(() => {
          this.isProcessing = false;
        });
    },
    stageErrors(index, field) {
      const key = `stages.${index}.${field}`;
      const err = this.validationErrors[key];
      return Array.isArray(err) ? err : err ? [err] : [];
    },
    getErrorMessage(error) {
      if (error && error.errors && typeof error.errors === "object") {
        const firstErrorField = Object.keys(error.errors)[0];
        const firstErrorList = error.errors[firstErrorField];

        if (Array.isArray(firstErrorList) && firstErrorList.length) {
          return firstErrorList[0];
        }
      }

      if (error && error.message) {
        return error.message;
      }

      return this.$t("GENERAL.UNEXPECTED_ERROR");
    },
    setValidationErrors(error) {
      this.validationErrors = (error && error.errors) || {};
    },
    clearValidationError(field) {
      if (!this.validationErrors[field]) {
        return;
      }

      this.$delete(this.validationErrors, field);
    },
    clearStageValidationErrors() {
      Object.keys(this.validationErrors).forEach((key) => {
        if (key.startsWith("stages.")) {
          this.$delete(this.validationErrors, key);
        }
      });
    },
    clearErrors() {
      this.clearValidationError("name");
      this.clearStageValidationErrors();
    },
    nextStageOrder() {
      const max = this.stages.reduce(
        (m, s) => Math.max(m, Number(s.order) || 0),
        -1
      );
      return max + 1;
    },
    addStage() {
      this.stages.push({
        _key: ++stageKeySeq,
        name: "",
        order: this.nextStageOrder(),
      });
      this.clearErrors();
    },
    removeStage(index) {
      if (this.stages.length <= 2) {
        return;
      }
      this.stages.splice(index, 1);
      this.clearErrors();
    },
    buildStagesPayload() {
      return this.stages.map((s) => {
        let orderVal = parseInt(s.order, 10);
        if (!Number.isFinite(orderVal) || orderVal < 0) {
          orderVal = 0;
        }
        const row = {
          name: (s.name || "").trim(),
          order: orderVal,
        };
        if (this.$route.params.id && s.id != null && s.id !== "") {
          row.id = s.id;
        }
        return row;
      });
    },
    back() {
      if (this.hasPermissions("pipelines-view-all")) {
        this.$router.push({ name: "pipelines" });
      } else {
        this.$router.push({ name: "dashboard" });
      }
    },
    store() {
      this.$refs.requestData.validate();
      if (!this.validRequestData) {
        return;
      }
      this.validationErrors = {};

      const stagesPayload = this.buildStagesPayload();
      if (stagesPayload.length < 2) {
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("PIPELINES.MIN_STAGES"),
          type: "error",
        });
        return;
      }

      const body = {
        name: (this.requestData.name || "").trim(),
        active: !!this.requestData.active,
        stages: stagesPayload,
      };

      let action = CREATE_PIPELINE;
      let dispatchPayload = body;
      if (this.$route.params.id) {
        action = UPDATE_PIPELINE;
        dispatchPayload = {
          id: this.$route.params.id,
          params: body,
        };
      }

      this.isProcessing = true;
      this.$store
        .dispatch(action, dispatchPayload)
        .then((response) => {
          return this.$store.dispatch(GET_PIPELINE_OPTIONS, {}).then(() => response);
        })
        .then((response) => {
          userStateService.setState(
            "table-state.pipelines-table.blinkRowItemKey",
            response.id
          );

          this.$store.dispatch(SHOW_SNACK, {
            message: this.$t("GENERAL.SUCCESS_MESSAGE"),
          });

          if (this.$route.params.id) {
            this.$router.push({ name: "pipelines" });
            return;
          }

          this.$router.push({
            name: "pipelines-view",
            params: { id: String(response.id) },
          });
        })
        .catch((error) => {
          this.setValidationErrors(error);
          this.$store.dispatch(SHOW_SNACK, {
            message: this.getErrorMessage(error),
            type: "error",
          });
        })
        .finally(() => {
          this.isProcessing = false;
        });
    },
  },
};
</script>
